import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { AuthLayout } from '@/components/layout/auth-layout';
import { useOperatorLogin } from '@/lib/auth';

const OperatorLoginSchema = z.object({
  token: z.string().min(8, 'Token operatora jest wymagany'),
});
type OperatorLoginInput = z.infer<typeof OperatorLoginSchema>;

export const Route = createFileRoute('/operator/login')({
  staticData: { public: true },
  component: OperatorLoginPage,
});

function OperatorLoginPage() {
  const navigate = useNavigate();
  const login = useOperatorLogin();

  const form = useForm<OperatorLoginInput>({
    resolver: zodResolver(OperatorLoginSchema),
    defaultValues: { token: '' },
  });

  const onSubmit = async (data: OperatorLoginInput) => {
    try {
      await login.mutateAsync(data);
      navigate({ to: '/operator' });
    } catch {
      const message = 'Nieprawidłowy token operatora';
      form.setError('token', { message });
      toast.error(message);
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Logowanie operatora</CardTitle>
          <CardDescription>
            Wpisz token bearer operatora. Logowanie kliniki odbywa się przez stronę{' '}
            <a href="/login" className="text-accent underline-offset-4 hover:underline">
              /login
            </a>
            .
          </CardDescription>
        </CardHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="token">Token operatora</Label>
              <Input
                id="token"
                type="password"
                autoComplete="off"
                {...form.register('token')}
                aria-invalid={!!form.formState.errors.token}
              />
              {form.formState.errors.token ? (
                <p className="text-xs text-error">{form.formState.errors.token.message}</p>
              ) : null}
            </div>
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? 'Logowanie…' : 'Zaloguj się'}
            </Button>
          </CardContent>
        </form>
      </Card>
    </AuthLayout>
  );
}
